// deps
import React, { memo } from "react";

// constants
import {
    LOTTIE_ANIMATIONS,
    LOTTIE_ANIMATIONS_CONGRATS_PROPS,
    LOTTIE_ANIMATIONS_SMILEY_PROPS,
    ONE_WHITE_CLOUDINARY_URL,
} from "@dineout-payment-success-ui/constants/LottieAnimations";
import { INR_CURRENCY_SYMBOL_TEXT } from "@dineout-payment-success-ui/constants/Constants";

// Types
import { IOneBenefitsCard } from "@dineout-payment-success-ui/interfaces";

// helpers
import { LottieAnimation, getCloudinaryUrl, format, useEffectOnce } from "@swiggy-private/core-ui";

// styles
import {
    CongratsAnimWrapper,
    OneBenefitsCardWrapper,
    SmileyAnimWrapper,
    OneBenefitsText,
    OneLogo,
    AmountWrapper,
} from "./styles";

import { PAYMENT_SUCCESS_ONE_BENEFITS_CARD } from "@swiggy-private/payment-test-ids";
import { useDineoutPaymentSuccess } from "@dineout-payment-success-ui/hooks/useDineoutPaymentSuccess";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

const OneBenefitsCard: React.FC<IOneBenefitsCard> = ({
    savingsValue,
    successAnimationData,
    userStatus,
    oneLogoStyle,
    oneLogoUrl,
    styles,
    orderId,
}) => {
    const { onSavingsTextImpression } = useDineoutPaymentSuccess();
    useEffectOnce(() => {
        const savingsText = `${format(INR_CURRENCY_SYMBOL_TEXT, savingsValue || "0")} ${
            successAnimationData?.bottomText1
        } ${successAnimationData?.bottomText2}`;
        onSavingsTextImpression({ text: savingsText, orderId: orderId });
    });
    return (
        <OneBenefitsCardWrapper
            backgroundImage={styles?.backgroundGradientImage}
            data-testid="one-benefits-card-wrapper"
        >
            <CongratsAnimWrapper>
                <LottieAnimation
                    {...LOTTIE_ANIMATIONS_CONGRATS_PROPS}
                    fileName={getCloudinaryUrl(LOTTIE_ANIMATIONS.CONGRATS_LOTTIE)}
                    visible={true}
                    playLottie
                />
            </CongratsAnimWrapper>
            <AmountWrapper>
                {savingsValue && (
                    <>
                        <OneBenefitsText
                            variant="V2.heading2.neutral.black"
                            color={(styles?.bottomTextColor as keyof IColors) || "background_Primary"}
                            data-testid={PAYMENT_SUCCESS_ONE_BENEFITS_CARD.SAVINGS_VALUE}
                            isMarginRight
                        >
                            {format(INR_CURRENCY_SYMBOL_TEXT, savingsValue || "0")}
                        </OneBenefitsText>
                        {successAnimationData?.bottomText1 ? (
                            <OneBenefitsText
                                variant="V2.subheading1.neutral.medium"
                                color={(styles?.bottomTextColor as keyof IColors) || "background_Primary"}
                                isMarginRight
                                data-testid={PAYMENT_SUCCESS_ONE_BENEFITS_CARD.BOTTOM_TEXT_1}
                            >
                                {successAnimationData?.bottomText1}
                            </OneBenefitsText>
                        ) : null}
                    </>
                )}
                {successAnimationData?.bottomText2 && (
                    <>
                        <OneBenefitsText
                            variant="V2.subheading1.neutral.medium"
                            color={(styles?.bottomTextColor as keyof IColors) || "text_Button_Primary"}
                            isMarginRight
                            data-testid={PAYMENT_SUCCESS_ONE_BENEFITS_CARD.BOTTOM_TEXT_2}
                        >
                            {successAnimationData?.bottomText2}
                        </OneBenefitsText>
                        {userStatus && (
                            <OneLogo
                                src={getCloudinaryUrl(oneLogoUrl || ONE_WHITE_CLOUDINARY_URL)}
                                width={"auto"}
                                height={`${oneLogoStyle?.height || 13.5}`}
                                isMarginRight={Boolean(successAnimationData?.bottomText3)}
                            />
                        )}
                        {successAnimationData?.bottomText3 && (
                            <OneBenefitsText
                                variant="V2.subheading1.neutral.medium"
                                color={(styles?.bottomTextColor as keyof IColors) || "text_Button_Primary"}
                                data-testid={PAYMENT_SUCCESS_ONE_BENEFITS_CARD.BOTTOM_TEXT_3}
                            >
                                {successAnimationData?.bottomText3}
                            </OneBenefitsText>
                        )}
                    </>
                )}
            </AmountWrapper>
            <SmileyAnimWrapper>
                <LottieAnimation
                    {...LOTTIE_ANIMATIONS_SMILEY_PROPS}
                    fileName={getCloudinaryUrl(LOTTIE_ANIMATIONS.SMILEY_LOTTIE)}
                    visible={true}
                    playLottie
                />
            </SmileyAnimWrapper>
        </OneBenefitsCardWrapper>
    );
};

export default memo(OneBenefitsCard);
